import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface BuyerReportDocument {
  id: string;
  report_id: string;
  team_id: string;
  file_name: string;
  file_description: string | null;
  storage_path: string | null;
  public_url: string | null;
  link_url: string | null;
  file_size_bytes: number | null;
  created_at: string;
}

export interface BuyerReport {
  id: string;
  team_id: string;
  user_id: string;

  // Property identification
  address: string;
  city: string | null;
  state: string | null;
  zip: string | null;
  zpid: string | null;
  zillow_url: string | null;

  // Property data
  property_data: any;
  price_history: any[] | null;
  tax_history: any[] | null;
  schools: any[] | null;
  walk_transit_data: any | null;
  fema_flood_zone: string | null;

  // AI analysis
  ai_analysis_structured: any;
  suggested_offer_low: number | null;
  suggested_offer_high: number | null;
  ai_offer_strategy: any;

  // Customization
  buyer_name: string | null;
  custom_agent_name: string | null;
  custom_agent_headshot_url: string | null;
  custom_logo_url: string | null;
  primary_brand_color: string;
  secondary_brand_color: string;
  agent_notes: string | null;

  // Publishing
  is_published: boolean;
  published_at: string | null;

  created_at: string;
  updated_at: string;
}

export function useBuyerReportQuery(reportId: string | undefined) {
  return useQuery({
    queryKey: ["property-report", reportId],
    queryFn: async () => {
      if (!reportId) throw new Error("No report ID");
      const { data, error } = await supabase
        .from("property_reports")
        .select("*")
        .eq("id", reportId)
        .single();
      if (error) throw error;
      return data as BuyerReport;
    },
    enabled: !!reportId,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
  });
}

export function useBuyerReportListQuery(teamId: string | undefined) {
  return useQuery({
    queryKey: ["property-report-list", teamId],
    queryFn: async () => {
      if (!teamId) throw new Error("No team ID");
      const { data, error } = await supabase
        .from("property_reports")
        .select("*")
        .eq("team_id", teamId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data || []) as BuyerReport[];
    },
    enabled: !!teamId,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
  });
}

export function useBuyerReportUpdateMutation(reportId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (updates: Partial<BuyerReport>) => {
      const { data, error } = await supabase
        .from("property_reports")
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq("id", reportId)
        .select()
        .single();
      if (error) throw error;
      return data as BuyerReport;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["property-report", reportId], data);
    },
  });
}

export function useBuyerReportDocumentsQuery(reportId: string | undefined) {
  return useQuery({
    queryKey: ["buyer-report-documents", reportId],
    queryFn: async () => {
      if (!reportId) throw new Error("No report ID");
      const { data, error } = await supabase
        .from("property_report_documents")
        .select("*")
        .eq("report_id", reportId)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data || []) as BuyerReportDocument[];
    },
    enabled: !!reportId,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
  });
}

export function useAddBuyerReportDocumentMutation(reportId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (doc: Omit<BuyerReportDocument, "id" | "report_id" | "created_at">) => {
      const { data, error } = await supabase
        .from("property_report_documents")
        .insert([{ ...doc, report_id: reportId }])
        .select()
        .single();
      if (error) throw error;
      return data as BuyerReportDocument;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["buyer-report-documents", reportId] });
    },
  });
}

export function useDeleteBuyerReportDocumentMutation(reportId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (doc: BuyerReportDocument) => {
      if (doc.storage_path) {
        await supabase.storage.from("logos").remove([doc.storage_path]);
      }
      const { error } = await supabase
        .from("property_report_documents")
        .delete()
        .eq("id", doc.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["buyer-report-documents", reportId] });
    },
  });
}
